/**
 * BugReportsPage - Epic Bug Reports
 *
 * Pagina de gestao de Ocorrencias (Bug Reports) do sistema.
 * Visivel apenas para MASTER users.
 *
 * Duas tabs:
 * 1. Ocorrencias: lista, filtros, dashboard stats, detalhe, criacao
 * 2. Categorias: CRUD das categorias de bug reports
 */
import React, { useState, useEffect, useCallback } from 'react';
import { MainLayout } from '../components/MainLayout';
import { Plus, RefreshCw, Bug } from 'lucide-react';
import { useBugReports } from '../hooks/useBugReports';
import {
  BugReport,
  BugReportFilters as Filters,
  Criticidade,
} from '../services/bugReportService';
import { BugReportStats } from '../components/bug-reports/BugReportStats';
import { BugReportFilters as FiltersComponent } from '../components/bug-reports/BugReportFilters';
import { BugReportTable } from '../components/bug-reports/BugReportTable';
import { BugReportDetailModal } from '../components/bug-reports/BugReportDetailModal';
import { BugReportCreateModal } from '../components/bug-reports/BugReportCreateModal';
import { CategoriasTab } from '../components/bug-reports/CategoriasTab';

type Tab = 'ocorrencias' | 'categorias';

const BugReportsPage: React.FC = () => {
  const [tab, setTab] = useState<Tab>('ocorrencias');
  const [filters, setFilters] = useState<Filters>({ page: 1, page_size: 20 });
  const [selectedReport, setSelectedReport] = useState<BugReport | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const {
    bugReports,
    categorias,
    stats,
    loading,
    error,
    totalPages,
    total,
    currentPage,
    fetchBugReports,
    fetchStats,
    fetchCategorias,
    updateBugReport,
  } = useBugReports();

  const reload = useCallback(() => {
    if (tab === 'ocorrencias') {
      fetchBugReports(filters);
      fetchStats();
    }
    fetchCategorias(false); // sempre carrega categorias (para selectors)
  }, [tab, filters, fetchBugReports, fetchStats, fetchCategorias]);

  useEffect(() => {
    reload();
  }, [reload]);

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const handleFilterCriticidade = (c: Criticidade) => {
    setFilters((prev) => ({ ...prev, criticidade: prev.criticidade === c ? undefined : c, page: 1 }));
  };

  const handleUpdated = async (updated: BugReport) => {
    setSelectedReport(updated);
    await fetchBugReports(filters);
    await fetchStats();
  };

  const handleCreated = () => {
    reload();
  };

  return (
    <MainLayout>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Bug className="w-7 h-7 text-red-600" />
              Bug Reports (Ocorrencias)
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Gestao centralizada de problemas reportados pelo sistema offline e online
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={reload}
              disabled={loading}
              className="p-2 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50"
              title="Recarregar"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            {tab === 'ocorrencias' && (
              <button
                type="button"
                onClick={() => setCreateModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm font-medium"
              >
                <Plus className="w-4 h-4" /> Nova Ocorrencia
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-4">
          <nav className="flex gap-1">
            <button
              type="button"
              onClick={() => setTab('ocorrencias')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === 'ocorrencias'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              Ocorrencias
            </button>
            <button
              type="button"
              onClick={() => setTab('categorias')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === 'categorias'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              Categorias
            </button>
          </nav>
        </div>

        {/* Conteudo da aba */}
        {tab === 'ocorrencias' ? (
          <>
            <BugReportStats stats={stats} onFilterClick={handleFilterCriticidade} />

            <FiltersComponent
              filters={filters}
              categorias={categorias}
              onChange={setFilters}
              onReset={() => setFilters({ page: 1, page_size: 20 })}
            />

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
                {error}
              </div>
            )}

            <BugReportTable
              bugReports={bugReports}
              loading={loading}
              currentPage={currentPage}
              totalPages={totalPages}
              total={total}
              onRowClick={setSelectedReport}
              onPageChange={handlePageChange}
            />
          </>
        ) : (
          <CategoriasTab />
        )}

        {/* Modais */}
        <BugReportDetailModal
          bugReport={selectedReport}
          onClose={() => setSelectedReport(null)}
          onUpdated={handleUpdated}
        />

        {createModalOpen && (
          <BugReportCreateModal
            categorias={categorias}
            onClose={() => setCreateModalOpen(false)}
            onCreated={handleCreated}
            onCategoriasChanged={() => fetchCategorias(false)}
          />
        )}
      </div>
    </MainLayout>
  );
};

export default BugReportsPage;
